import { FC, ReactNode, useCallback, useRef, useState } from 'react';
import { Col, Row } from 'antd';
import { TbDatabase, TbInfoCircle } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDataSources } from '@/hooks/useDataSources';
import { EmptySection, FlexContentRegion, FullHeightFlexColumn, VirtualizedList as List } from '@/shared';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { DataSourceOption, SetStateMethod } from '@/types';

import AddDataSourceListItem from './components/AddDataSourceListItem';
import ManageDataSourceListitem from './components/ManageDataSourceListitem';

interface DataSourceListProps {
    view: 'add' | 'manage';
    category: string;
    options: DataSourceOption[];
    setOpen: SetStateMethod<boolean>;
    loading?: boolean;
}

const DataSourceList: FC<DataSourceListProps> = ({
    category,
    options,
    view,
    setOpen,
    loading = false,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [pendingOptionKey, setPendingOptionKey] = useState<string>();
    const pendingOptionKeyRef = useRef<string>();

    const beginConnecting = useCallback((optionKey: string) => {
        if (pendingOptionKeyRef.current) {
            return false;
        }

        pendingOptionKeyRef.current = optionKey;
        setPendingOptionKey(optionKey);
        return true;
    }, []);

    const finishConnecting = useCallback((optionKey: string) => {
        if (pendingOptionKeyRef.current !== optionKey) {
            return;
        }

        pendingOptionKeyRef.current = undefined;
        setPendingOptionKey(undefined);
    }, []);

    const { dataSources } = useDataSources();

    const selectedSectionHint = dataSources?.find((i) => i.key === category)?.hint;

    const viewMap = (view: 'add' | 'manage', i: DataSourceOption, index: number): ReactNode => {
        const map = {
            add: (
                <AddDataSourceListItem
                    i={i}
                    indx={index}
                    interactionDisabled={Boolean(pendingOptionKey)}
                    connecting={pendingOptionKey === String(i.key)}
                    setOpen={setOpen}
                    onBeginConnecting={beginConnecting}
                    onFinishConnecting={finishConnecting}
                />
            ),
            manage: <ManageDataSourceListitem i={i} indx={index} />,
        } as Record<typeof view, ReactNode>;

        return map[view];
    };

    if (loading) {
        return (
            <section
                style={{
                    height: '100%',
                    padding: '0.75rem',
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    marginTop: '0.5rem',
                }}
            >
                <BaseSkeleton title={false} paragraph={{ rows: 6 }} />
            </section>
        );
    }

    return options?.length !== 0 ? (
        <FullHeightFlexColumn>
            <Row style={{ marginBottom: '0.75rem' }}>
                {/* <Col flex='none'>
                    <Button size='small' style={{ padding: '0 0.25rem' }}>
                        <ListFilter size='1rem' />
                        Filter
                    </Button>
                </Col> */}
                {/* <Col flex='auto'></Col> */}
                <Col flex='none'>
                    <div>
                        <small style={{ color: token.colorTextTertiary }}>{options?.length} Results</small>
                    </div>
                </Col>
            </Row>
            <FlexContentRegion
                overflowY='auto'
                style={{
                    padding: '0.5rem 0.75rem',
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                }}
            >
                <List
                    dataSource={options}
                    renderItem={(i: DataSourceOption, indx: number) => {
                        return viewMap(view, i, indx);
                    }}
                />
                <div style={{ marginBottom: '0.5rem' }}>
                    <small style={{ color: token.colorTextTertiary }}>
                        <TbInfoCircle style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />
                        All images are the property of their respective owners.
                    </small>
                </div>
                {view === 'add' && selectedSectionHint}
            </FlexContentRegion>
        </FullHeightFlexColumn>
    ) : (
        <section
            style={{
                height: '100%',
                padding: '0.5rem 0.75rem',
                border: `1px dashed ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                marginTop: '0.5rem',
            }}
        >
            <EmptySection
                height='100%'
                icon={<TbDatabase size='75%' style={{ color: token.colorTextTertiary }} />}
                label='No Data Sources Found'
                description='Try refining your search for better results.'
            />
        </section>
    );
};

export default DataSourceList;
